import CanvasContainer from "../../canvas/CanvasContainer.tsx";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import html2canvas from "html2canvas";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import jsPDF from "jspdf";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import ThreeDRotationOutlinedIcon from "@mui/icons-material/ThreeDRotationOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import WallpaperOutlinedIcon from "@mui/icons-material/WallpaperOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { openAppConfirm } from "../../components/AppDialogs";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import bgAurora from "../../assets/presntationBG/bg-aurora.svg";
import bgEmeraldBands from "../../assets/presntationBG/bg-emerald-bands.svg";
import bgGrid from "../../assets/presntationBG/bg-grid.svg";
import bgLilacOrbit from "../../assets/presntationBG/bg-lilac-orbit.svg";
import bgMidnightFrame from "../../assets/presntationBG/bg-midnight-frame.svg";
import bgMintPaper from "../../assets/presntationBG/bg-mint-paper.svg";
import bgNavyBeams from "../../assets/presntationBG/bg-navy-beams.svg";
import bgObsidianGrid from "../../assets/presntationBG/bg-obsidian-grid.svg";
import bgSandTiles from "../../assets/presntationBG/bg-sand-tiles.svg";
import bgSkyCurves from "../../assets/presntationBG/bg-sky-curves.svg";
import bgSlateRings from "../../assets/presntationBG/bg-slate-rings.svg";
import bgSoftBlueRibbon from "../../assets/presntationBG/bg-soft-blue-ribbon.svg";
import bgTealGlow from "../../assets/presntationBG/bg-teal-glow.svg";
import bgWaves from "../../assets/presntationBG/bg-waves.svg";
import "./map.css";
import "./presentationMaker.css";
import {
  AddSlideToPresentationCall,
  GetAllSlideWithContentByTitleId,
  AddContentToSlidePresentationCall,
  EditContentToSlidePresentationCall,
  DeletePresentationSlideCall,
} from "../../redux/api/universities.api.js";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { store } from "../../redux/store.js";
import { useDispatch, useSelector } from "react-redux";
import {
  setSlides,
  setCurrentSlide,
  updateCurrentSlideContent,
  updateSlideContentById,
} from "../../redux/slices/uniSlice";
import {
  GetTitlesCall,
  UpdatePresentationPublishCall,
  EditSubTitlesCall,
} from "../../redux/api/title.api";
import MDButton from "components/MDButton";
import {
  GetMyUploadsCall,
  UploadMyFileCall,
  DeleteMyUploadCall,
  getPersistedUploads,
  persistUploads,
} from "../../redux/api/uploads.api";

const PRESENTATION_RELOAD_KEY = "presentation-maker-auto-play";
const PPTX_SCRIPT_ID = "pptxgenjs-browser-script";

const buildSolidBackgroundSvg = (color) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720"><rect width="1280" height="720" fill="${color}"/></svg>`
  )}`;

let pptxScriptPromise = null;

const loadPptxGenJS = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("PptxGenJS can only load in the browser"));
  }
  if (window.PptxGenJS) {
    return Promise.resolve(window.PptxGenJS);
  }
  if (pptxScriptPromise) {
    return pptxScriptPromise;
  }

  pptxScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(PPTX_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.PptxGenJS), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load PowerPoint export library")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = PPTX_SCRIPT_ID;
    script.src = "https://cdn.jsdelivr.net/npm/pptxgenjs@4.0.1/dist/pptxgen.min.js";
    script.async = true;
    script.onload = () => resolve(window.PptxGenJS);
    script.onerror = () => reject(new Error("Failed to load PowerPoint export library"));
    document.body.appendChild(script);
  });

  return pptxScriptPromise;
};

function PresentationMakerPage() {
  const cloneCanvasData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    try {
      return JSON.parse(JSON.stringify(data));
    } catch (error) {
      return data.map((item) => ({ ...item }));
    }
  }, []);

  const { t } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allSlides = useSelector((state) => state.uni?.slides);
  const allTitles = useSelector((state) => {
    const st = state.title;
    if (!st) return [];
    return Array.isArray(st.titles) ? st.titles : [];
  });
  const currentLang = useSelector((state) => state.general?.lang || "en");
  const university = useSelector((state) => state.auth?.universityInfo);
  const isDark = theme.palette.mode === "dark";
  const currentSlide = useSelector((state) => state.uni?.currentSlide);
  const toolbarActionsRef = useRef(null);
  const stageAreaRef = useRef(null);
  const editStageFrameRef = useRef(null);
  const initialCanvasData = currentSlide?.content?.data
    ? (Array.isArray(currentSlide.content.data) ? currentSlide.content.data : [])
    : [];
  const WEB_EDIT_STAGE_WIDTH = 1280;
  const WEB_EDIT_STAGE_HEIGHT = 720;
  const MOBILE_EDIT_STAGE_WIDTH = 414;
  const MOBILE_EDIT_STAGE_HEIGHT = 800;
  const PLAY_STAGE_WIDTH = 1280;
  const PLAY_STAGE_HEIGHT = 720;
  const FRAME_BORDER_SIZE = 28;
  const EXPORT_STAGE_WIDTH = PLAY_STAGE_WIDTH + FRAME_BORDER_SIZE;
  const EXPORT_STAGE_HEIGHT = PLAY_STAGE_HEIGHT + FRAME_BORDER_SIZE;
  // Clamp displayed stage size so layout stays consistent on very small or very large screens
  const MIN_STAGE_DISPLAY_WIDTH = 640;
  const MIN_STAGE_DISPLAY_HEIGHT = 360;
  const MAX_STAGE_DISPLAY_WIDTH = 1600;
  const MAX_STAGE_DISPLAY_HEIGHT = 900;
  const latestSaveRequestRef = useRef(0);
  const latestCanvasSnapshotRef = useRef([]);
  const exportStageRef = useRef(null);

  const hasValidId = id && id !== "undefined";
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const hasEnsuredSlideRef = useRef(false);
  const [fileMenuAnchor, setFileMenuAnchor] = useState(null);
  const [publishMenuAnchor, setPublishMenuAnchor] = useState(null);
  const [publishStatus, setPublishStatus] = useState("unpublished");
  const [editScale, setEditScale] = useState(1);
  const [viewMode, setViewMode] = useState("web"); // "web" | "mobile"
  const EDIT_STAGE_WIDTH =
    viewMode === "mobile" ? MOBILE_EDIT_STAGE_WIDTH : WEB_EDIT_STAGE_WIDTH;
  const EDIT_STAGE_HEIGHT =
    viewMode === "mobile" ? MOBILE_EDIT_STAGE_HEIGHT : WEB_EDIT_STAGE_HEIGHT;
  const editStageFrameW = EDIT_STAGE_WIDTH + FRAME_BORDER_SIZE;
  const editStageFrameH = EDIT_STAGE_HEIGHT + FRAME_BORDER_SIZE;
  const [activePanel, setActivePanel] = useState(null);
  const [selectedBgColor, setSelectedBgColor] = useState("#F8FAFC");
  const [isExporting, setIsExporting] = useState(false);
  const [exportSlidePayload, setExportSlidePayload] = useState(null);
  const [exportSlideKey, setExportSlideKey] = useState("export-slide");
  const backgroundOptions = useMemo(
    () => [
      { key: "aurora", labelKey: "pages.presentationMaker.aurora", src: bgAurora },
      { key: "soft-blue-ribbon", labelKey: "pages.presentationMaker.softBlueRibbon", src: bgSoftBlueRibbon },
      { key: "sky-curves", labelKey: "pages.presentationMaker.skyCurves", src: bgSkyCurves },
      { key: "grid", labelKey: "pages.presentationMaker.grid", src: bgGrid },
      { key: "mint-paper", labelKey: "pages.presentationMaker.mintPaper", src: bgMintPaper },
      { key: "sand-tiles", labelKey: "pages.presentationMaker.sandTiles", src: bgSandTiles },
      { key: "lilac-orbit", labelKey: "pages.presentationMaker.lilacOrbit", src: bgLilacOrbit },
      { key: "waves", labelKey: "pages.presentationMaker.waves", src: bgWaves },
      { key: "midnight-frame", labelKey: "pages.presentationMaker.midnightFrame", src: bgMidnightFrame },
      { key: "slate-rings", labelKey: "pages.presentationMaker.slateRings", src: bgSlateRings },
      { key: "navy-beams", labelKey: "pages.presentationMaker.navyBeams", src: bgNavyBeams },
      { key: "obsidian-grid", labelKey: "pages.presentationMaker.obsidianGrid", src: bgObsidianGrid },
      { key: "teal-glow", labelKey: "pages.presentationMaker.tealGlow", src: bgTealGlow },
      { key: "emerald-bands", labelKey: "pages.presentationMaker.emeraldBands", src: bgEmeraldBands },
    ],
    []
  );
  const backgroundColorOptions = useMemo(
    () => [
      "#FFFFFF",
      "#F8FAFC",
      "#E0F2FE",
      "#DBEAFE",
      "#EDE9FE",
      "#ECFDF5",
      "#FEF3C7",
      "#FEE2E2",
      "#0F172A",
      "#111827",
    ],
    []
  );

  // Uploads side-panel state (private media library)
  const [uploadsTab, setUploadsTab] = useState("images");
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadsUploading, setUploadsUploading] = useState(false);
  const [uploadsSearch, setUploadsSearch] = useState("");
  const uploadsInputRef = useRef(null);
  const [draggedSlideId, setDraggedSlideId] = useState(null);

  // Ensure presentation titles are in Redux so we can resolve publish state from is_published
  useEffect(() => {
    GetTitlesCall(currentLang, "presentation");
  }, [currentLang]);

  // When navigating between different subtitles (presentations), make sure
  // the currentSlide always belongs to the latest slides list for that subtitle.
  // Otherwise the editor can keep showing the previous subtitle until the user
  // manually clicks a thumbnail.
  useEffect(() => {
    if (!Array.isArray(allSlides) || allSlides.length === 0) {
      if (currentSlide) {
        dispatch(setCurrentSlide(null));
      }
      return;
    }
    const existsInCurrentList = currentSlide
      ? allSlides.some((s) => String(s.id) === String(currentSlide.id))
      : false;
    if (!existsInCurrentList) {
      dispatch(setCurrentSlide(allSlides[0]));
    }
  }, [allSlides, currentSlide?.id, dispatch]);

  // Find the specific presentation subtitle (session) by route param id
  const presentationSubtitle = useMemo(() => {
    if (!id || !Array.isArray(allTitles)) return null;
    for (const t of allTitles) {
      if (Array.isArray(t.subtitles)) {
        const sub = t.subtitles.find((s) => String(s.id) === String(id));
        if (sub) return sub;
      }
    }
    return null;
  }, [allTitles, id]);

  const presentationInternalName = useMemo(() => {
    const locs = presentationSubtitle?.titleLocalizations;
    if (!Array.isArray(locs)) return t("pages.presentationTitles.untitledPresentation");
    const titleLoc = locs.find((e) => e.field === "title");
    return (
      String(titleLoc?.value || "").trim() ||
      t("pages.presentationTitles.untitledPresentation")
    );
  }, [presentationSubtitle, t]);

  const [editingPresentationName, setEditingPresentationName] = useState(false);
  const [presentationNameDraft, setPresentationNameDraft] = useState("");

  useEffect(() => {
    if (!editingPresentationName) {
      setPresentationNameDraft(presentationInternalName);
    }
  }, [presentationInternalName, editingPresentationName]);

  const savePresentationInternalName = useCallback(async () => {
    const next = String(presentationNameDraft || "").trim();
    setEditingPresentationName(false);
    if (!id || !next || next === presentationInternalName) return;
    await EditSubTitlesCall({
      id,
      title: next,
      lang: currentLang,
      type: "presentation",
    });
  }, [presentationNameDraft, id, presentationInternalName, currentLang]);

  // Sync local publishStatus from the subtitle's is_published field
  useEffect(() => {
    if (!presentationSubtitle) return;
    setPublishStatus(
      presentationSubtitle.is_published ? "published" : "unpublished"
    );
  }, [presentationSubtitle]);

  const handleUpdatePublishStatus = async (nextStatus) => {
    // Close menu immediately for snappy UI, then sync with backend + Redux.
    setPublishMenuAnchor(null);
    const targetId = id;
    if (!targetId) return;

    const body = {
      title_id: targetId,
      is_published: nextStatus === "published",
    };

    await UpdatePresentationPublishCall(body);
    // Refresh presentation titles so is_published is updated in Redux.
    await GetTitlesCall(currentLang, "presentation");
    // publishStatus will be updated by the useEffect that watches presentationTitle.
  };

  const fetchUploads = useCallback(async () => {
    try {
      setUploadsLoading(true);
      // Only this user's presentation library, not slide or system files they also own.
      const list = await GetMyUploadsCall("user_upload");
      const persisted = getPersistedUploads();
      const byUrl = new Map();
      persisted.forEach((item) => byUrl.set(item.url, item));
      (list || []).forEach((item) => byUrl.set(item.url, item));
      const merged = Array.from(byUrl.values());
      // When API returns empty, still show persisted so refresh keeps showing sessionStorage uploads
      setUploads((prev) =>
        merged.length > 0 ? merged : (persisted.length > 0 ? persisted : prev)
      );
    } catch (error) {
      console.error("Error loading uploads:", error);
      const persisted = getPersistedUploads();
      if (persisted.length > 0) setUploads(persisted);
    } finally {
      setUploadsLoading(false);
    }
  }, []);

  const handleUploadsFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setUploadsUploading(true);
    try {
      const url = await UploadMyFileCall(file, "user_upload");
      if (url) {
        const item = {
          id: `local-${Date.now()}`,
          url,
          name: file.name,
          alias: file.name,
          type: file.type || (file.name.match(/\.(mp4|webm|mov)$/i) ? "video/mp4" : "image/png"),
        };
        setUploads((prev) => {
          const next = [...prev, item];
          persistUploads(next);
          return next;
        });
        // Refetch so the list gets the server record (real id for delete) and stays in sync
        fetchUploads();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setUploadsUploading(false);
      if (uploadsInputRef.current) {
        uploadsInputRef.current.value = "";
      }
    }
  };

  const filteredUploads = useMemo(() => {
    const q = uploadsSearch.trim().toLowerCase();
    let list = uploads;
    if (uploadsTab === "images") {
      list = list.filter((f) => f.type?.startsWith("image/"));
    } else if (uploadsTab === "videos") {
      list = list.filter((f) => f.type?.startsWith("video/"));
    } else {
      list = list.filter(
        (f) => f.type?.startsWith("image/") || f.type?.startsWith("video/")
      );
    }
    if (!q) return list;
    return list.filter((f) => {
      const name = (f.alias || f.name || "").toLowerCase();
      return name.includes(q);
    });
  }, [uploads, uploadsSearch, uploadsTab]);

  useEffect(() => {
    hasEnsuredSlideRef.current = false;
    setInitialLoadDone(false);
  }, [id]);

  useEffect(() => {
    if (!hasValidId) return;
    let cancelled = false;
    GetAllSlideWithContentByTitleId(id).then(() => {
      if (!cancelled) setInitialLoadDone(true);
    });
    return () => { cancelled = true; };
  }, [hasValidId, id]);

  // AI assistant may append slides while this page is open — reload on confirm.
  useEffect(() => {
    if (!hasValidId) return undefined;
    const onReload = (event) => {
      const targetId = event?.detail?.id;
      if (targetId && String(targetId) !== String(id)) return;
      GetAllSlideWithContentByTitleId(id);
    };
    window.addEventListener("microlab:presentation-reload", onReload);
    return () => window.removeEventListener("microlab:presentation-reload", onReload);
  }, [hasValidId, id]);

  useEffect(() => {
    if (!hasValidId || !initialLoadDone || hasEnsuredSlideRef.current) return;
    if (!allSlides || allSlides.length > 0) return;
    hasEnsuredSlideRef.current = true;
    const university = store.getState()?.auth?.universityInfo;
    AddSlideToPresentationCall({
      id,
      title_id: id,
      title: 1,
      university_id: university?.university_id,
    });
  }, [id, hasValidId, initialLoadDone, allSlides]);

  useEffect(() => {
    if (hasValidId) return;
    navigate("/presentationTitles", { replace: true });
  }, [hasValidId, navigate]);

  useEffect(() => {
    latestCanvasSnapshotRef.current = cloneCanvasData(initialCanvasData);
  }, [cloneCanvasData, initialCanvasData]);

  const waitForRenderFrame = useCallback(
    () =>
      new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.setTimeout(resolve, 350);
          });
        });
      }),
    []
  );

  const generateThumbnailForSlide = useCallback(
    async (slideId, canvasData) => {
      try {
        // Use the same off-screen export stage as PDF export so we capture
        // the slide content reliably (edit stage has iframes/video html2canvas can't capture).
        setExportSlidePayload(Array.isArray(canvasData) ? canvasData : []);
        setExportSlideKey(`thumb-${slideId}-${Date.now()}`);
        await waitForRenderFrame();
        if (!exportStageRef.current) return;
        const canvas = await html2canvas(exportStageRef.current, {
          backgroundColor: null,
          scale: 0.25,
          useCORS: true,
          allowTaint: true,
          logging: false,
        });
        const thumbnail = canvas.toDataURL("image/png");
        setExportSlidePayload(null);
        const latestSlides = store.getState().uni?.slides ?? [];
        const updated = latestSlides.map((slide) =>
          slide?.id === slideId ? { ...slide, thumbnail } : slide
        );
        dispatch(setSlides(updated));
      } catch (error) {
        console.error("Thumbnail capture failed:", error);
        setExportSlidePayload(null);
      }
    },
    [dispatch, waitForRenderFrame]
  );

  const handleSaveSlide = async (slideMeta, canvasData) => {
    const slideId = slideMeta?.id;
    const presentationContentId = slideMeta?.presentation_content_id;
    if (!slideId || !hasValidId) return;

    // Avoid unnecessary PUT/POST on first load or when nothing actually changed.
    const latestSlidesBeforeSave = store.getState().uni?.slides ?? [];
    const existingSlide = latestSlidesBeforeSave.find((s) => s?.id === slideId);
    const prevData = existingSlide?.content?.data;
    try {
      const nextJson = JSON.stringify(Array.isArray(canvasData) ? canvasData : []);
      const prevJson = JSON.stringify(Array.isArray(prevData) ? prevData : []);
      if (nextJson === prevJson && presentationContentId) {
        // Content is identical to what we already have persisted – skip API call.
        return;
      }
    } catch (e) {
      // If comparison fails for any reason, fall through and save normally.
    }
    const saveRequestId = latestSaveRequestRef.current + 1;
    latestSaveRequestRef.current = saveRequestId;
    latestCanvasSnapshotRef.current = cloneCanvasData(canvasData);

    // Apply locally first so editor and presenter stay in sync immediately.
    dispatch(updateSlideContentById({ slideId, data: canvasData }));

    try {
      if (presentationContentId) {
        await EditContentToSlidePresentationCall({
          content_id: presentationContentId,
          data: canvasData,
        });
      } else {
        await AddContentToSlidePresentationCall({
          slide_id: slideId,
          data: canvasData,
        });
      }
      // Ignore stale completions from older save requests.
      if (saveRequestId !== latestSaveRequestRef.current) {
        return;
      }
      generateThumbnailForSlide(slideId, canvasData);
    } catch (error) {
      console.error("Error saving slide content:", error);
    }
  };

  const handleSlideSelect = (slide) => {
    dispatch(setCurrentSlide(slide));
  };

  const generatedThumbnailIdsRef = useRef(new Set());
  // Generate thumbnail for current slide when it has content but no thumbnail (e.g. after load).
  useEffect(() => {
    const slide = currentSlide;
    if (!slide?.id || slide.thumbnail || generatedThumbnailIdsRef.current.has(slide.id))
      return;
    const data = slide?.content?.data;
    if (!Array.isArray(data) || data.length === 0) return;
    generatedThumbnailIdsRef.current.add(slide.id);
    const t = setTimeout(() => {
      generateThumbnailForSlide(slide.id, data);
    }, 800);
    return () => clearTimeout(t);
  }, [currentSlide?.id, currentSlide?.thumbnail, generateThumbnailForSlide]);

  const handleDeleteSlide = async (slide) => {
    if (!hasValidId || !slide?.id) return;
    const confirmed = await openAppConfirm({
      title: t("pages.presentationMaker.deleteSlideConfirm"),
      message: t("pages.presentationMaker.deleteSlideConfirm"),
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!confirmed) return;
    const deletedId = slide.id;
    const res = await DeletePresentationSlideCall(slide.id, id);
    if (res?.status === 200 || res?.status === 201) {
      if (currentSlide?.id === deletedId) {
        const updatedSlides = store.getState().uni?.slides ?? [];
        dispatch(setCurrentSlide(updatedSlides[0] ?? null));
      }
    }
  };

  const onMousePlus = useCallback(async () => {
    if (!hasValidId) return;
    const university = store.getState()?.auth?.universityInfo;
    const body = {
      id,
      title_id: id,
      title: 1,
      university_id: university?.university_id,
    };
    AddSlideToPresentationCall(body);
  }, [hasValidId, id]);

  const [isPlayMode, setIsPlayMode] = useState(false);
  const [playSlideIndex, setPlaySlideIndex] = useState(0);
  const slideBeforePlayIdRef = useRef(null);
  const [playSnapshotData, setPlaySnapshotData] = useState(null);
  const [playSnapshotSlideId, setPlaySnapshotSlideId] = useState(null);
  const [playSlidesSnapshot, setPlaySlidesSnapshot] = useState([]);

  const [playScale, setPlayScale] = useState(1);

  const addTextPreset = useCallback((preset) => {
    const presets = {
      textbox: {
        dimension: { width: "360px", height: "80px" },
        position: { left: 120, top: 120 },
        content:
          '<p><span style="font-size: 28px; font-family: Segoe UI, Arial, sans-serif; color: rgb(15, 23, 42);">Add a text box</span></p>',
      },
      heading: {
        dimension: { width: "720px", height: "110px" },
        position: { left: 110, top: 90 },
        content:
          '<p><strong style="font-size: 58px; font-family: Segoe UI, Arial, sans-serif; color: rgb(15, 23, 42);">Add a heading</strong></p>',
      },
      subheading: {
        dimension: { width: "620px", height: "84px" },
        position: { left: 120, top: 220 },
        content:
          '<p><strong style="font-size: 34px; font-family: Segoe UI, Arial, sans-serif; color: rgb(30, 41, 59);">Add a subheading</strong></p>',
      },
      body: {
        dimension: { width: "560px", height: "72px" },
        position: { left: 120, top: 330 },
        content:
          '<p><span style="font-size: 24px; font-family: Segoe UI, Arial, sans-serif; color: rgb(71, 85, 105);">Add a little bit of body text</span></p>',
      },
    };
    const nextPreset = presets[preset];
    if (!nextPreset) return;
    toolbarActionsRef.current?.addElement?.("TEXT", nextPreset);
  }, []);

  const toolGroups = useMemo(
    () => [
      {
        key: "bg",
        labelKey: "pages.presentationMaker.bg",
        icon: <WallpaperOutlinedIcon />,
        onClick: () => setActivePanel((prev) => (prev === "bg" ? null : "bg")),
      },
      {
        key: "uploads",
        labelKey: "pages.presentationMaker.uploads",
        icon: <CloudUploadOutlinedIcon />,
        onClick: () => setActivePanel((prev) => (prev === "uploads" ? null : "uploads")),
      },
      {
        key: "text",
        labelKey: "pages.presentationMaker.text",
        icon: <TextFieldsOutlinedIcon />,
        onClick: () => setActivePanel((prev) => (prev === "text" ? null : "text")),
      },
      {
        key: "image",
        labelKey: "pages.presentationMaker.image",
        icon: <AddPhotoAlternateOutlinedIcon />,
        onClick: () => {
          setActivePanel(null);
          toolbarActionsRef.current?.addElement?.("IMAGE");
        },
      },
      {
        key: "video",
        labelKey: "pages.presentationMaker.video",
        icon: <VideoLibraryOutlinedIcon />,
        onClick: () => {
          setActivePanel(null);
          toolbarActionsRef.current?.addElement?.("VIDEO");
        },
      },
      {
        key: "slide",
        labelKey: "pages.presentationMaker.slide",
        icon: <SlideshowOutlinedIcon />,
        onClick: () => {
          setActivePanel(null);
          toolbarActionsRef.current?.addElement?.("SLIDE");
        },
      },
      {
        key: "model",
        labelKey: "pages.presentationMaker.model3d",
        icon: <ThreeDRotationOutlinedIcon />,
        onClick: () => {
          setActivePanel(null);
          toolbarActionsRef.current?.addElement?.("3D_MODEL");
        },
      },
    ],
    []
  );

  const currentSlideIndex = Math.max(
    0,
    allSlides?.findIndex?.((slide) => slide?.id === currentSlide?.id) ?? 0
  );

  useEffect(() => {
    if (!isPlayMode) return;
    const playFrameW = PLAY_STAGE_WIDTH + FRAME_BORDER_SIZE;
    const playFrameH = PLAY_STAGE_HEIGHT + FRAME_BORDER_SIZE;
    const updateScale = () => {
      const minScaleBySize = Math.max(
        MIN_STAGE_DISPLAY_WIDTH / playFrameW,
        MIN_STAGE_DISPLAY_HEIGHT / playFrameH
      );
      const maxScaleBySize = Math.min(
        MAX_STAGE_DISPLAY_WIDTH / playFrameW,
        MAX_STAGE_DISPLAY_HEIGHT / playFrameH
      );
      const rawScale = Math.min(
        window.innerWidth / playFrameW,
        window.innerHeight / playFrameH,
        3.2
      );
      const scale = Math.max(
        minScaleBySize,
        Math.min(rawScale, maxScaleBySize, 3.2)
      );
      setPlayScale(scale);
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [isPlayMode]);

  useEffect(() => {
    const updateScale = () => {
      const stageNode = stageAreaRef.current;
      if (!stageNode) return;
      const availableWidth = Math.max(stageNode.clientWidth - 16, 320);
      const availableHeight = Math.max(stageNode.clientHeight - 16, 220);
      let maxEditScale = 1.45;
      if (window.innerWidth <= 1440) maxEditScale = 1.05;
      if (window.innerWidth <= 1280) maxEditScale = 0.9;
      if (window.innerWidth <= 1180) maxEditScale = 0.82;
      const minScaleBySize = Math.max(
        MIN_STAGE_DISPLAY_WIDTH / editStageFrameW,
        MIN_STAGE_DISPLAY_HEIGHT / editStageFrameH
      );
      const maxScaleBySize = Math.min(
        MAX_STAGE_DISPLAY_WIDTH / editStageFrameW,
        MAX_STAGE_DISPLAY_HEIGHT / editStageFrameH
      );
      const rawScale = Math.min(
        availableWidth / editStageFrameW,
        availableHeight / editStageFrameH,
        maxEditScale
      );
      const scale = Math.max(
        minScaleBySize,
        Math.min(rawScale, maxScaleBySize, maxEditScale)
      );
      setEditScale(scale);
    };
    updateScale();
    const observer =
      typeof ResizeObserver !== "undefined" && stageAreaRef.current
        ? new ResizeObserver(updateScale)
        : null;
    if (observer && stageAreaRef.current) {
      observer.observe(stageAreaRef.current);
    }
    window.addEventListener("resize", updateScale);
    return () => {
      window.removeEventListener("resize", updateScale);
      observer?.disconnect();
    };
  }, [activePanel]);

  // Load uploads once when uploads panel is opened; seed from sessionStorage so refresh keeps list
  useEffect(() => {
    if (activePanel !== "uploads") return;
    const persisted = getPersistedUploads();
    if (persisted.length > 0) setUploads((prev) => (prev.length === 0 ? persisted : prev));
    fetchUploads();
  }, [activePanel, fetchUploads]);

  const startPlayMode = useCallback((options = {}) => {
    if (!allSlides?.length) return;
    const targetSlideId = options.slideId ?? currentSlide?.id ?? null;
    const targetSlideIndex =
      typeof options.slideIndex === "number"
        ? Math.max(0, Math.min(options.slideIndex, allSlides.length - 1))
        : currentSlideIndex;
    const liveCanvasData = cloneCanvasData(
      options.canvasData ??
        toolbarActionsRef.current?.getCanvasData?.() ??
        latestCanvasSnapshotRef.current
    );
    const snapshotSlides = (allSlides ?? []).map((slide) => {
      const slideData = cloneCanvasData(slide?.content?.data);
      if (
        slide?.id &&
        targetSlideId &&
        slide.id === targetSlideId &&
        Array.isArray(liveCanvasData)
      ) {
        return {
          ...slide,
          content: { ...(slide.content || {}), data: liveCanvasData },
        };
      }
      return {
        ...slide,
        content: slide?.content ? { ...slide.content, data: slideData } : slide?.content,
      };
    });
    setPlaySlidesSnapshot(snapshotSlides);
    if (Array.isArray(liveCanvasData) && targetSlideId) {
      dispatch(updateSlideContentById({ slideId: targetSlideId, data: liveCanvasData }));
      setPlaySnapshotData(liveCanvasData);
      setPlaySnapshotSlideId(targetSlideId);
    } else {
      setPlaySnapshotData(null);
      setPlaySnapshotSlideId(null);
    }
    slideBeforePlayIdRef.current = targetSlideId;
    setIsPlayMode(true);
    setPlaySlideIndex(targetSlideIndex);
  }, [allSlides, cloneCanvasData, currentSlide, currentSlideIndex, dispatch]);

  const requestRefreshBeforePlay = useCallback(() => {
    if (!allSlides?.length || !hasValidId) return;
    const liveCanvasData = cloneCanvasData(
      toolbarActionsRef.current?.getCanvasData?.() ?? latestCanvasSnapshotRef.current
    );
    try {
      sessionStorage.setItem(
        PRESENTATION_RELOAD_KEY,
        JSON.stringify({
          presentationId: id,
          slideId: currentSlide?.id ?? null,
          slideIndex: currentSlideIndex,
          canvasData: Array.isArray(liveCanvasData) ? liveCanvasData : [],
        })
      );
    } catch (error) {
      startPlayMode();
      return;
    }
    window.location.reload();
  }, [allSlides?.length, cloneCanvasData, currentSlide?.id, currentSlideIndex, hasValidId, id, startPlayMode]);

  useEffect(() => {
    if (!hasValidId || !initialLoadDone || !allSlides?.length) return;
    let payload;
    try {
      const rawPayload = sessionStorage.getItem(PRESENTATION_RELOAD_KEY);
      if (!rawPayload) return;
      payload = JSON.parse(rawPayload);
      sessionStorage.removeItem(PRESENTATION_RELOAD_KEY);
    } catch (error) {
      try {
        sessionStorage.removeItem(PRESENTATION_RELOAD_KEY);
      } catch (removeError) {}
      return;
    }
    if (payload?.presentationId !== id) return;
    startPlayMode({
      slideId: payload?.slideId ?? null,
      slideIndex: typeof payload?.slideIndex === "number" ? payload.slideIndex : 0,
      canvasData: payload?.canvasData,
    });
  }, [allSlides?.length, hasValidId, id, initialLoadDone, startPlayMode]);

  const exitPlayMode = useCallback(() => {
    setIsPlayMode(false);
    setPlaySnapshotData(null);
    setPlaySnapshotSlideId(null);
    setPlaySlidesSnapshot([]);
    const restoreSlideId = slideBeforePlayIdRef.current;
    if (restoreSlideId) {
      const latestSlides = store.getState().uni?.slides ?? [];
      const latestSlide = latestSlides.find((slide) => slide?.id === restoreSlideId) ?? null;
      dispatch(setCurrentSlide(latestSlide));
    }
    slideBeforePlayIdRef.current = null;
  }, [dispatch]);

  useEffect(() => {
    if (!isPlayMode) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        exitPlayMode();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (playSlideIndex < allSlides.length - 1) {
          setPlaySlideIndex((i) => i + 1);
        } else {
          exitPlayMode();
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (playSlideIndex > 0) {
          setPlaySlideIndex((i) => Math.max(0, i - 1));
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPlayMode, exitPlayMode, playSlideIndex, allSlides?.length]);

  const playStageRef = useRef(null);

  const handlePlayOverlayClick = useCallback(
    (event) => {
      // If the click happened inside the active stage frame (including iframes),
      // treat it as an interaction with the slide, not a navigation request.
      if (event && playStageRef.current) {
        const rect = playStageRef.current.getBoundingClientRect();
        const { clientX, clientY } = event;
        if (
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          return;
        }
      }

      if (playSlideIndex < allSlides.length - 1) {
        setPlaySlideIndex((i) => i + 1);
      } else {
        exitPlayMode();
      }
    },
    [playSlideIndex, allSlides?.length, exitPlayMode]
  );

  const playSlidesSource = isPlayMode && playSlidesSnapshot.length ? playSlidesSnapshot : allSlides;
  const playSlide = isPlayMode ? playSlidesSource?.[playSlideIndex] : null;
  const playSlideData =
    playSlide?.id && playSlide.id === playSnapshotSlideId && Array.isArray(playSnapshotData)
      ? playSnapshotData
      : playSlide?.content?.data;
  const playInitialData = Array.isArray(playSlideData) ? playSlideData : [];
  const scaledEditWidth = (EDIT_STAGE_WIDTH + FRAME_BORDER_SIZE) * editScale;
  const scaledEditHeight = (EDIT_STAGE_HEIGHT + FRAME_BORDER_SIZE) * editScale;
  const scaledPlayWidth = (PLAY_STAGE_WIDTH + FRAME_BORDER_SIZE) * playScale;
  const scaledPlayHeight = (PLAY_STAGE_HEIGHT + FRAME_BORDER_SIZE) * playScale;
  const brandLogoSrc =
    university?.logo_url || require("../../assets/images/logos/mini_logo.png");

  const handleExportPresentation = useCallback(() => {
    const payload = {
      id,
      exported_at: new Date().toISOString(),
      slides: allSlides ?? [],
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `presentation-${id || "export"}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    setFileMenuAnchor(null);
  }, [allSlides, id]);

  const getSlidesForExport = useCallback(() => {
    const liveCanvasData = cloneCanvasData(
      toolbarActionsRef.current?.getCanvasData?.() ?? latestCanvasSnapshotRef.current
    );
    return (allSlides ?? []).map((slide) => {
      const slideData =
        slide?.id && currentSlide?.id && slide.id === currentSlide.id
          ? liveCanvasData
          : cloneCanvasData(slide?.content?.data);
      return {
        ...slide,
        content: {
          ...(slide?.content || {}),
          data: Array.isArray(slideData) ? slideData : [],
        },
      };
    });
  }, [allSlides, cloneCanvasData, currentSlide?.id]);

  const captureSlideImage = useCallback(
    async (slide, index) => {
      const slideData = Array.isArray(slide?.content?.data) ? slide.content.data : [];
      setExportSlidePayload(slideData);
      setExportSlideKey(`${slide?.id ?? "slide"}-${index}-${Date.now()}`);
      await waitForRenderFrame();
      if (!exportStageRef.current) {
        throw new Error("Export stage is not ready");
      }
      const canvas = await html2canvas(exportStageRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      return canvas.toDataURL("image/png");
    },
    [waitForRenderFrame]
  );

  const handleExportPdf = useCallback(async () => {
    if (!allSlides?.length || isExporting) return;
    setFileMenuAnchor(null);
    setIsExporting(true);
    try {
      const slides = getSlidesForExport();
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [EXPORT_STAGE_WIDTH, EXPORT_STAGE_HEIGHT],
      });

      for (let index = 0; index < slides.length; index += 1) {
        const imageData = await captureSlideImage(slides[index], index);
        if (index > 0) {
          pdf.addPage([EXPORT_STAGE_WIDTH, EXPORT_STAGE_HEIGHT], "landscape");
        }
        pdf.addImage(imageData, "PNG", 0, 0, EXPORT_STAGE_WIDTH, EXPORT_STAGE_HEIGHT);
      }

      pdf.save(`presentation-${id || "export"}.pdf`);
    } catch (error) {
      console.error("PDF export failed:", error);
    } finally {
      setIsExporting(false);
      setExportSlidePayload(null);
    }
  }, [
    EXPORT_STAGE_HEIGHT,
    EXPORT_STAGE_WIDTH,
    allSlides?.length,
    captureSlideImage,
    getSlidesForExport,
    id,
    isExporting,
  ]);

  const handleExportPowerPoint = useCallback(async () => {
    if (!allSlides?.length || isExporting) return;
    setFileMenuAnchor(null);
    setIsExporting(true);
    try {
      const PptxGenJS = await loadPptxGenJS();
      const slides = getSlidesForExport();
      const pptx = new PptxGenJS();
      pptx.defineLayout({
        name: "MICROLAB_PRESENTATION",
        width: 13.08,
        height: 7.48,
      });
      pptx.layout = "MICROLAB_PRESENTATION";
      pptx.author = "Microlab";
      pptx.subject = "Presentation export";
      pptx.title = `presentation-${id || "export"}`;

      for (let index = 0; index < slides.length; index += 1) {
        const imageData = await captureSlideImage(slides[index], index);
        const pptSlide = pptx.addSlide();
        pptSlide.background = { color: "FFFFFF" };
        pptSlide.addImage({
          data: imageData,
          x: 0,
          y: 0,
          w: 13.08,
          h: 7.48,
        });
      }

      await pptx.writeFile({ fileName: `presentation-${id || "export"}.pptx` });
    } catch (error) {
      console.error("PowerPoint export failed:", error);
    } finally {
      setIsExporting(false);
      setExportSlidePayload(null);
    }
  }, [allSlides?.length, captureSlideImage, getSlidesForExport, id, isExporting]);

  const applyBackgroundToAllSlides = useCallback(
    async (content) => {
      if (!content || !hasValidId) return;

      const slides = store.getState().uni?.slides ?? [];
      const stamp = Date.now();
      const makeBackground = (slideId) => ({
        type: "IMAGE",
        id: `BG__${slideId || "slide"}__${stamp}`,
        position: { top: 0, left: 0 },
        dimension: {
          width: `${WEB_EDIT_STAGE_WIDTH}px`,
          height: `${WEB_EDIT_STAGE_HEIGHT}px`,
        },
        content,
        isBackground: true,
      });
      const withBackground = (canvasData, slideId) => {
        const rest = (Array.isArray(canvasData) ? canvasData : []).filter(
          (item) => !(item?.type === "IMAGE" && item?.isBackground)
        );
        return [makeBackground(slideId), ...rest];
      };

      if (!slides.length) {
        toolbarActionsRef.current?.setBackgroundImage?.(content);
        return;
      }

      const currentId = currentSlide?.id;
      const liveBase =
        (currentId && toolbarActionsRef.current?.getCanvasData?.()) ||
        currentSlide?.content?.data ||
        [];

      const updates = slides.map((slide) => {
        const slideId = slide?.id;
        const baseData =
          slideId && slideId === currentId ? liveBase : slide?.content?.data || [];
        return { slide, nextData: withBackground(baseData, slideId) };
      });

      const currentUpdate = updates.find((u) => u.slide?.id === currentId);
      if (currentUpdate && toolbarActionsRef.current?.setCanvasData) {
        toolbarActionsRef.current.setCanvasData(currentUpdate.nextData);
        toolbarActionsRef.current.setActiveSelection?.(new Set());
      }

      await Promise.all(
        updates.map(async ({ slide, nextData }) => {
          const slideId = slide?.id;
          if (!slideId) return;
          dispatch(updateSlideContentById({ slideId, data: nextData }));
          try {
            if (slide.presentation_content_id) {
              await EditContentToSlidePresentationCall({
                content_id: slide.presentation_content_id,
                data: nextData,
              });
            } else {
              await AddContentToSlidePresentationCall({
                slide_id: slideId,
                data: nextData,
              });
            }
            generateThumbnailForSlide(slideId, nextData);
          } catch (error) {
            console.error("Failed to apply background to slide", slideId, error);
          }
        })
      );
    },
    [
      currentSlide?.content?.data,
      currentSlide?.id,
      dispatch,
      generateThumbnailForSlide,
      hasValidId,
    ]
  );

  const applySolidBackground = useCallback(
    (color) => {
      setSelectedBgColor(color);
      applyBackgroundToAllSlides(buildSolidBackgroundSvg(color));
    },
    [applyBackgroundToAllSlides]
  );

  if (!hasValidId) {
    return null;
  }

  return (
    <DashboardLayout>
      <Box
        className="content presentation-maker-root"
        sx={{
          p: 0,
          bgcolor: isDark ? "#10131c" : "#eef1f6",
        }}
      >
        {!isPlayMode && (
          <>
            <Box className="presentation-maker-topbar">
              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => navigate("/presentationTitles")}
                  aria-label={t("pages.presentationMaker.backToPresentations")}
                  sx={{ color: "white", opacity: 0.92 }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box
                  component="img"
                  src={brandLogoSrc}
                  alt={university?.name || "Microlab"}
                  className="presentation-maker-brand"
                />
                {editingPresentationName ? (
                  <TextField
                    size="small"
                    value={presentationNameDraft}
                    autoFocus
                    onChange={(e) => setPresentationNameDraft(e.target.value)}
                    onBlur={savePresentationInternalName}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        savePresentationInternalName();
                      }
                      if (e.key === "Escape") {
                        setEditingPresentationName(false);
                        setPresentationNameDraft(presentationInternalName);
                      }
                    }}
                    sx={{
                      minWidth: 160,
                      maxWidth: 280,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(255,255,255,0.14)",
                        color: "white",
                        fontWeight: 650,
                        fontSize: "0.9rem",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.28)" },
                        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                        "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.7)" },
                      },
                      "& .MuiInputBase-input": { py: 0.6, px: 1.1 },
                    }}
                  />
                ) : (
                  <Box
                    className="presentation-maker-title"
                    onClick={() => {
                      setPresentationNameDraft(presentationInternalName);
                      setEditingPresentationName(true);
                    }}
                    title={t("pages.presentationMaker.renamePresentation")}
                    sx={{
                      cursor: "text",
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                    }}
                  >
                    {presentationInternalName}
                  </Box>
                )}
                <MDButton
                  variant="text"
                  onClick={(event) => setFileMenuAnchor(event.currentTarget)}
                  sx={{
                    color: "white !important",
                    textTransform: "none",
                    fontWeight: 650,
                    fontSize: "0.85rem",
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  {t("pages.presentationMaker.file")}
                </MDButton>
                <MDButton
                  variant="text"
                  onClick={(event) => setPublishMenuAnchor(event.currentTarget)}
                  sx={{
                    color: "white !important",
                    textTransform: "none",
                    fontWeight: 650,
                    fontSize: "0.85rem",
                    minWidth: "auto",
                    px: 1.25,
                    borderRadius: 999,
                    backgroundColor:
                      publishStatus === "published"
                        ? "rgba(16,185,129,0.18)"
                        : "rgba(15,23,42,0.18)",
                    "&:hover": {
                      backgroundColor:
                        publishStatus === "published"
                          ? "rgba(16,185,129,0.28)"
                          : "rgba(15,23,42,0.28)",
                    },
                  }}
                >
                  {t("pages.presentationMaker.publish")}
                </MDButton>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  display: { xs: "none", md: "flex" },
                  px: 1,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: "rgba(15,23,42,0.28)",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => toolbarActionsRef.current?.undo?.()}
                  sx={{ color: "white" }}
                  aria-label={t("pages.presentationMaker.undo")}
                >
                  <UndoOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => toolbarActionsRef.current?.redo?.()}
                  sx={{ color: "white" }}
                  aria-label={t("pages.presentationMaker.redo")}
                >
                  <RedoOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    borderRadius: 999,
                    padding: "2px",
                    backgroundColor: "rgba(15,23,42,0.45)",
                  }}
                >
                  {[
                    { key: "web", label: "Web" },
                    { key: "mobile", label: "Mobile" },
                  ].map((mode) => {
                    const active = viewMode === mode.key;
                    return (
                      <button
                        key={mode.key}
                        type="button"
                        onClick={() => setViewMode(mode.key)}
                        style={{
                          border: "none",
                          outline: "none",
                          backgroundColor: active ? "#0ea5e9" : "transparent",
                          color: active ? "#0b1120" : "rgba(248,250,252,0.85)",
                          borderRadius: 999,
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          minWidth: 56,
                        }}
                      >
                        {mode.label}
                      </button>
                    );
                  })}
                </Box>
                <MDButton
                  variant="contained"
                  color="info"
                  startIcon={<PlayArrowIcon />}
                  onClick={requestRefreshBeforePlay}
                  disabled={!allSlides?.length}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 2,
                    py: 0.75,
                    backgroundColor: "rgba(255,255,255,0.14)",
                    color: "white !important",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.22)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("pages.presentationMaker.present")}
                </MDButton>
              </Box>
            </Box>

            <Menu
              anchorEl={fileMenuAnchor}
              open={Boolean(fileMenuAnchor)}
              onClose={() => setFileMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 3,
                  minWidth: 220,
                  boxShadow: "0 20px 45px rgba(15,23,42,0.18)",
                },
              }}
            >
              {[
                {
                  icon: <DescriptionOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.createNewPresentation",
                  onClick: () => {
                    setFileMenuAnchor(null);
                    navigate("/presentationTitles");
                  },
                },
                {
                  icon: <DriveFileRenameOutlineIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.renamePresentation",
                  onClick: () => {
                    setFileMenuAnchor(null);
                    setPresentationNameDraft(presentationInternalName);
                    setEditingPresentationName(true);
                  },
                },
                {
                  icon: <UndoOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.undo",
                  hint: "Ctrl+Z",
                  onClick: () => {
                    toolbarActionsRef.current?.undo?.();
                    setFileMenuAnchor(null);
                  },
                },
                {
                  icon: <RedoOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.redo",
                  hint: "Ctrl+Shift+Z",
                  onClick: () => {
                    toolbarActionsRef.current?.redo?.();
                    setFileMenuAnchor(null);
                  },
                },
                {
                  icon: <DownloadOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.exportJson",
                  disabled: isExporting,
                  onClick: handleExportPresentation,
                },
                {
                  icon: <DownloadOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.exportPdf",
                  disabled: isExporting,
                  onClick: handleExportPdf,
                },
                {
                  icon: <DownloadOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.exportPptx",
                  disabled: isExporting,
                  onClick: handleExportPowerPoint,
                },
                {
                  icon: <LogoutOutlinedIcon fontSize="small" />,
                  labelKey: "pages.presentationMaker.exit",
                  onClick: () => {
                    setFileMenuAnchor(null);
                    navigate("/presentationTitles");
                  },
                },
              ].map((item) => (
              <MenuItem
                key={item.labelKey}
                onClick={item.onClick}
                disabled={item.disabled}
                sx={{
                  py: 1,
                  px: 1.5,
                  minHeight: 42,
                  gap: 0.05,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 22,
                    color: "#64748b",
                    "& svg": {
                      fontSize: 18,
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={t(item.labelKey)}
                  primaryTypographyProps={{
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    color: "#334155",
                    lineHeight: 1.2,
                  }}
                />
                {item.hint && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      ml: 2,
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: "#64748b",
                    }}
                  >
                    {item.hint}
                  </Typography>
                )}
              </MenuItem>
              ))}
            </Menu>
            <Menu
              anchorEl={publishMenuAnchor}
              open={Boolean(publishMenuAnchor)}
              onClose={() => setPublishMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 3.5,
                  minWidth: 290,
                  p: 1,
                  boxShadow: "0 22px 50px rgba(15,23,42,0.2)",
                  border: "1px solid rgba(148,163,184,0.18)",
                  backdropFilter: "blur(14px)",
                },
              }}
            >
              <MenuItem
                selected={publishStatus === "published"}
                onClick={() => handleUpdatePublishStatus("published")}
                sx={{
                  borderRadius: 2.5,
                  py: 1.25,
                  px: 1.25,
                  mb: 0.5,
                  alignItems: "center",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(16,185,129,0.12)",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(16,185,129,0.18)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38, color: "#059669" }}>
                  <PublicOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("pages.presentationMaker.publishedToStudents")}
                  secondary={t("pages.presentationMaker.publishedToStudentsDesc")}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: "0.95rem" }}
                  secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }}
                />
                {publishStatus === "published" && <CheckIcon sx={{ fontSize: 18, color: "#059669" }} />}
              </MenuItem>
              <MenuItem
                selected={publishStatus === "unpublished"}
                onClick={() => handleUpdatePublishStatus("unpublished")}
                sx={{
                  borderRadius: 2.5,
                  py: 1.25,
                  px: 1.25,
                  alignItems: "center",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(100,116,139,0.14)",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(100,116,139,0.2)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38, color: "#475569" }}>
                  <VisibilityOffOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("pages.presentationMaker.unpublishedToStudents")}
                  secondary={t("pages.presentationMaker.unpublishedToStudentsDesc")}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: "0.95rem" }}
                  secondaryTypographyProps={{ fontSize: "0.8rem", color: "text.secondary" }}
                />
                {publishStatus === "unpublished" && (
                  <CheckIcon sx={{ fontSize: 18, color: "#475569" }} />
                )}
              </MenuItem>
            </Menu>
          </>
        )}

        {!isPlayMode && (
          <Box className="presentation-maker-workspace">
            <Box className="presentation-tools-rail">
              {toolGroups.map((tool) => (
                <Box
                  key={tool.key}
                  className={`presentation-tool-card ${
                    activePanel === tool.key ? "selected" : ""
                  }`}
                  onClick={tool.onClick}
                >
                  <Box className="presentation-tool-icon">{tool.icon}</Box>
                  <Typography
                    className="presentation-tool-label"
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: "#334155",
                      lineHeight: 1.2,
                      textAlign: "center",
                    }}
                  >
                    {t(tool.labelKey)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {activePanel === "text" && (
              <Box className="presentation-side-panel">
                <Typography className="presentation-panel-heading">
                  {t("pages.presentationMaker.defaultTextStyles")}
                </Typography>
                <Box
                  className="presentation-text-preset heading"
                  onClick={() => addTextPreset("heading")}
                >
                  <Typography>{t("pages.presentationMaker.addHeading")}</Typography>
                </Box>
                <Box
                  className="presentation-text-preset subheading"
                  onClick={() => addTextPreset("subheading")}
                >
                  <Typography>{t("pages.presentationMaker.addSubheading")}</Typography>
                </Box>
                <Box
                  className="presentation-text-preset body"
                  onClick={() => addTextPreset("body")}
                >
                  <Typography>{t("pages.presentationMaker.addBodyText")}</Typography>
                </Box>
              </Box>
            )}

            {activePanel === "bg" && (
              <Box className="presentation-side-panel">
                <Typography className="presentation-panel-heading">
                  {t("pages.presentationMaker.presentationBackgrounds")}
                </Typography>
                <Typography className="presentation-panel-subheading">
                  {t("pages.presentationMaker.applyBackgroundDesc")}
                </Typography>
                <Box className="presentation-bg-color-panel">
                  <Box className="presentation-bg-color-header">
                    <Typography className="presentation-bg-color-title">
                      {t("pages.presentationMaker.solidColor")}
                    </Typography>
                    <Box className="presentation-bg-color-picker-wrap">
                      <input
                        type="color"
                        value={selectedBgColor}
                        onChange={(event) => applySolidBackground(event.target.value)}
                        className="presentation-bg-color-input"
                        aria-label="Pick presentation background color"
                      />
                    </Box>
                  </Box>
                  <Box className="presentation-bg-color-swatches">
                    {backgroundColorOptions.map((color) => (
                      <Box
                        key={color}
                        className={`presentation-bg-swatch ${
                          selectedBgColor.toLowerCase() === color.toLowerCase() ? "selected" : ""
                        }`}
                        sx={{ backgroundColor: color }}
                        onClick={() => applySolidBackground(color)}
                      />
                    ))}
                  </Box>
                </Box>
                <Box className="presentation-bg-grid">
                  {backgroundOptions.map((bg) => (
                    <Box
                      key={bg.key}
                      className="presentation-bg-card"
                      onClick={() => applyBackgroundToAllSlides(bg.src)}
                    >
                      <Box
                        className="presentation-bg-preview"
                        sx={{ backgroundImage: `url(${bg.src})` }}
                      />
                      <Typography className="presentation-bg-label">
                        {t(bg.labelKey)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {activePanel === "uploads" && (
              <Box className="presentation-side-panel">
                <Typography className="presentation-panel-heading">
                  {t("pages.presentationMaker.uploads")}
                </Typography>
                <Box mb={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("pages.presentationMaker.searchUploads")}
                    value={uploadsSearch}
                    onChange={(event) => setUploadsSearch(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ fontSize: 18, color: "rgba(148,163,184,0.9)" }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 999,
                        bgcolor: "#f8fafc",
                        fontSize: 13,
                      },
                    }}
                  />
                </Box>

                <Box mb={1.5} display="flex" gap={1}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => uploadsInputRef.current?.click()}
                    sx={{
                      flex: 1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      px: 1.5,
                      py: 0.5,
                    }}
                  >
                    {uploadsUploading ? t("pages.presentationMaker.uploading") : t("pages.presentationMaker.uploadFiles")}
                  </MDButton>
                  <input
                    ref={uploadsInputRef}
                    type="file"
                    accept="image/*,video/*"
                    style={{ display: "none" }}
                    onChange={handleUploadsFileChange}
                  />
                </Box>

                <Tabs
                  value={uploadsTab}
                  onChange={(_, v) => setUploadsTab(v)}
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{
                    minHeight: 32,
                    mb: 1.5,
                    "& .MuiTab-root": {
                      minHeight: 32,
                      textTransform: "none",
                      fontSize: 12,
                      fontWeight: 600,
                      paddingInline: 1.2,
                    },
                  }}
                >
                  <Tab
                    value="images"
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ImageIcon sx={{ fontSize: 16 }} />
                        <span>{t("pages.presentationMaker.images")}</span>
                      </Box>
                    }
                  />
                  <Tab
                    value="videos"
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <MovieIcon sx={{ fontSize: 16 }} />
                        <span>{t("pages.presentationMaker.videos")}</span>
                      </Box>
                    }
                  />
                </Tabs>

                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                  }}
                >
                  {uploadsLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 4,
                      }}
                    >
                      <CircularProgress size={22} />
                    </Box>
                  ) : filteredUploads.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 4,
                        gap: 0.5,
                        fontSize: 12,
                        color: "#64748b",
                      }}
                    >
                      <span>No uploads yet.</span>
                      <span>Use “Upload files” to add media.</span>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                        gap: 1,
                      }}
                    >
                      {filteredUploads.map((file) => {
                        const isImage = file.type?.startsWith("image/");
                        return (
                          <Box
                            key={file.id || file.url}
                            sx={{
                              position: "relative",
                              borderRadius: 2,
                              overflow: "hidden",
                              bgcolor: isImage ? "#e5e7eb" : "#020617",
                              cursor: "pointer",
                              border: "1px solid rgba(148,163,184,0.45)",
                            }}
                            onClick={() => {
                              if (isImage) {
                                toolbarActionsRef.current?.addElement?.("IMAGE", {
                                  content: file.url,
                                });
                              } else {
                                toolbarActionsRef.current?.addElement?.("VIDEO", {
                                  content: file.url,
                                });
                              }
                              setTimeout(() => {
                                toolbarActionsRef.current?.saveNow?.();
                              }, 100);
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                zIndex: 2,
                                bgcolor: "rgba(15,23,42,0.86)",
                                color: "#fff",
                                "&:hover": { bgcolor: "rgba(15,23,42,0.98)" },
                              }}
                              onClick={async (event) => {
                                event.stopPropagation();
                                if (!file.id) return;
                                const isLocal = String(file.id).startsWith("local-");
                                if (isLocal) {
                                  setUploads((prev) => {
                                    const next = prev.filter((f) => f.id !== file.id);
                                    persistUploads(next);
                                    return next;
                                  });
                                  return;
                                }
                                const ok = await openAppConfirm({
                                  title: t("pages.presentationMaker.deleteUploadConfirm"),
                                  message: t("pages.presentationMaker.deleteUploadConfirm"),
                                  confirmText: "Delete",
                                  confirmColor: "error",
                                });
                                if (!ok) return;
                                try {
                                  await DeleteMyUploadCall(file.id);
                                  setUploads((prev) => {
                                    const next = prev.filter((f) => f.id !== file.id);
                                    persistUploads(next);
                                    return next;
                                  });
                                } catch (error) {
                                  console.error("Failed to delete upload:", error);
                                }
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            {isImage ? (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 80,
                                  backgroundImage: `url(${file.url})`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 80,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#e5e7eb",
                                }}
                              >
                                <MovieIcon sx={{ fontSize: 26 }} />
                              </Box>
                            )}
                            <Box
                              sx={{
                                px: 0.75,
                                py: 0.5,
                                bgcolor: "rgba(15,23,42,0.9)",
                                color: "#f9fafb",
                                fontSize: 10,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {file.alias || file.name}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Box className="presentation-stage-area" ref={stageAreaRef}>
              <Box className="presentation-stage-shell">
                <Box
                  className="presentation-stage-scale"
                  sx={{
                    width: scaledEditWidth,
                    height: scaledEditHeight,
                    minWidth: MIN_STAGE_DISPLAY_WIDTH,
                    minHeight: MIN_STAGE_DISPLAY_HEIGHT,
                    maxWidth: MAX_STAGE_DISPLAY_WIDTH,
                    maxHeight: MAX_STAGE_DISPLAY_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                <Box
                  className="presentation-stage-frame"
                  ref={editStageFrameRef}
                  sx={{
                    width: EDIT_STAGE_WIDTH,
                    height: EDIT_STAGE_HEIGHT,
                    transform: `scale(${editScale})`,
                    transformOrigin: "center center",
                  }}
                >
                    <CanvasContainer
                      key={currentSlide?.id ?? "no-slide"}
                      initialData={initialCanvasData}
                      uploadEntity={{ entity_type: "presentation", entity_id: id }}
                      uploadsOnlyFromLibrary
                      onSave={(canvasData) =>
                        handleSaveSlide(
                          {
                            id: currentSlide?.id,
                            presentation_content_id: currentSlide?.presentation_content_id,
                          },
                          canvasData
                        )
                      }
                      stageWidth={EDIT_STAGE_WIDTH}
                      stageHeight={EDIT_STAGE_HEIGHT}
                      showAddButtons={false}
                      actionsRef={toolbarActionsRef}
                      viewMode={viewMode === "mobile" ? "mobile" : "web"}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {!isPlayMode && (
          <Box className="presentation-bottom-strip">
            <Box
              className="presentation-add-thumb"
              onClick={onMousePlus}
            >
              <AddIcon />
              <Typography>Add page</Typography>
            </Box>

            <Box className="presentation-thumbs-scroll">
              {(allSlides ?? []).map((slide, index) => {
                const isSelected = slide?.id === currentSlide?.id;
                const slideId = slide?.id ?? index;
                return (
                  <Box
                    key={slideId}
                    className={`presentation-thumb ${isSelected ? "selected" : ""}`}
                    onClick={() => handleSlideSelect(slide)}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      setDraggedSlideId(slideId);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      if (!draggedSlideId) return;
                      const latestSlides = store.getState().uni?.slides ?? [];
                      if (!latestSlides.length) return;
                      const fromIndex = latestSlides.findIndex(
                        (s, i) => (s?.id ?? i) === draggedSlideId
                      );
                      if (fromIndex < 0 || fromIndex === index) return;
                      const reordered = [...latestSlides];
                      const [moved] = reordered.splice(fromIndex, 1);
                      reordered.splice(index, 0, moved);
                      dispatch(setSlides(reordered));
                      setDraggedSlideId(null);
                    }}
                    onDragEnd={() => {
                      setDraggedSlideId(null);
                    }}
                  >
                    <Box className="presentation-thumb-number">{index + 1}</Box>
                    <Box
                      className="presentation-thumb-canvas"
                      sx={
                        slide?.thumbnail
                          ? {
                              backgroundImage: `url(${slide.thumbnail})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    />
                    <Typography className="presentation-thumb-label">
                      Slide {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      className="presentation-thumb-delete"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteSlide(slide);
                      }}
                    >
                      <AddIcon sx={{ transform: "rotate(45deg)", fontSize: 18 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>

            <Box className="presentation-strip-status">
              <Typography variant="body2" color="text.secondary">
                Page {currentSlideIndex + 1} / {allSlides?.length || 0}
              </Typography>
            </Box>
          </Box>
        )}

        {isPlayMode && (
          <Box
            onClick={handlePlayOverlayClick}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              bgcolor: "rgba(0,0,0,0.85)",
              display: "flex",
              cursor: "pointer",
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePlayOverlayClick();
              }
            }}
            aria-label="Click or press key to go to next slide"
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.6)",
                pointerEvents: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
                minHeight: 0,
                minWidth: 0,
                py: 2,
                px: 2,
              }}
            >
              <Box
                sx={{
                  width: scaledPlayWidth,
                  height: scaledPlayHeight,
                  minWidth: MIN_STAGE_DISPLAY_WIDTH,
                  minHeight: MIN_STAGE_DISPLAY_HEIGHT,
                  maxWidth: MAX_STAGE_DISPLAY_WIDTH,
                  maxHeight: MAX_STAGE_DISPLAY_HEIGHT,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                ref={playStageRef}
              >
                <Box
                  className="presentation-stage-frame presentation-stage-frame-play"
                  sx={{
                    width: PLAY_STAGE_WIDTH,
                    height: PLAY_STAGE_HEIGHT,
                    transform: `scale(${playScale})`,
                    transformOrigin: "center center",
                  }}
                >
                  {(playSlidesSource ?? []).map((slide, index) => {
                    const slideData =
                      slide?.id &&
                      slide.id === playSnapshotSlideId &&
                      Array.isArray(playSnapshotData)
                        ? playSnapshotData
                        : slide?.content?.data;
                    const initialData = Array.isArray(slideData) ? slideData : [];

                    return (
                      <Box
                        key={slide?.id ?? index}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          visibility: index === playSlideIndex ? "visible" : "hidden",
                          opacity: index === playSlideIndex ? 1 : 0,
                          pointerEvents: index === playSlideIndex ? "auto" : "none",
                        }}
                      >
                        <CanvasContainer
                          initialData={initialData}
                          isPlayMode
                          stageWidth={PLAY_STAGE_WIDTH}
                          stageHeight={PLAY_STAGE_HEIGHT}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: 18,
                right: 18,
                pointerEvents: "none",
                color: "rgba(255,255,255,0.92)",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.02em",
                px: 1.25,
                py: 0.6,
                borderRadius: 999,
                bgcolor: "rgba(15,23,42,0.48)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.22)",
              }}
            >
              {playSlideIndex + 1} / {allSlides?.length ?? 0}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            position: "fixed",
            left: -20000,
            top: 0,
            width: EXPORT_STAGE_WIDTH,
            height: EXPORT_STAGE_HEIGHT,
            pointerEvents: "none",
            opacity: 0,
            overflow: "hidden",
            zIndex: -1,
          }}
        >
          <Box
            ref={exportStageRef}
            className="presentation-stage-frame presentation-stage-frame-play"
            sx={{
              width: PLAY_STAGE_WIDTH,
              height: PLAY_STAGE_HEIGHT,
            }}
          >
            <CanvasContainer
              key={exportSlideKey}
              initialData={Array.isArray(exportSlidePayload) ? exportSlidePayload : []}
              isPlayMode
              stageWidth={PLAY_STAGE_WIDTH}
              stageHeight={PLAY_STAGE_HEIGHT}
            />
          </Box>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default PresentationMakerPage;
